import { useToast } from '@chakra-ui/react';

import { useCallback, useMemo } from 'react';
import { trpc } from '../../config/trpc';

type useFavoriteAirport = {
  pinned_hits: string;
  handleFavoriteClicked: (id: string) => () => void;
  isFavorite: (id: string) => boolean;
};

export const useFavoriteAirports = (): useFavoriteAirport => {
  const toast = useToast();

  const { data: favoriteAirports, refetch } = trpc.getUserFavoriteAirports.useQuery();
  const { mutateAsync: favoriteAirport } = trpc.favoriteAirport.useMutation();
  const { mutateAsync: unfavoriteAirport } = trpc.unfavoriteAirport.useMutation();

  const favoriteAirportIds = favoriteAirports?.map((airport) => airport.id) || [];
  const isFavorite = useCallback((airportId: string) => favoriteAirportIds?.includes(airportId), [favoriteAirportIds]);

  const onAirportClicked = useCallback(
    async (airportId: string) => {
      if (isFavorite(airportId)) {
        await unfavoriteAirport({ airportId });
      } else {
        if (favoriteAirportIds?.length >= 8) {
          toast({
            title: 'Too many favorites',
            description: 'You may only have 8 favorite airports',
            status: 'error',
          });
        } else await favoriteAirport({ airportId });
      }

      refetch();
    },
    [favoriteAirportIds, favoriteAirport, unfavoriteAirport],
  );

  const handleFavoriteClicked = (airportId: string) => () => onAirportClicked(airportId);

  const pinned_hits = useMemo(() => {
    if (!favoriteAirportIds.length) return '';

    return `${favoriteAirportIds.map((airportId: string, idx: number) => `${airportId}:${idx + 1}`)}`;
  }, [favoriteAirportIds]);

  return { pinned_hits, handleFavoriteClicked, isFavorite };
};
